import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Upload, Trash2, CheckCircle2, FileText, Eye, X, Loader2 } from "lucide-react";

const TYPES = [
  { value: "contrato_honorarios", label: "Contrato de Honorários" },
  { value: "procuracao",         label: "Procuração" },
];

const PLACEHOLDERS = [
  ["{{ CLIENTE }}", "Nome completo do cliente"],
  ["{{ CPF_CLIENTE }}", "CPF do cliente"],
  ["{{ RG_CLIENTE }}", "RG do cliente"],
  ["{{ ENDERECO_CLIENTE }}", "Endereço completo (do comprovante)"],
  ["{{ CIDADE }}", "Cidade (do comprovante)"],
  ["{{ ADVOGADO }}", "Nome do advogado"],
  ["{{ OAB }}", "OAB/UF nº"],
  ["{{ ESCRITORIO }}", "Nome do escritório"],
  ["{{ OBJETO }}", "Objeto do contrato (texto exato)"],
  ["{{ VALOR }}", "Valor dos honorários (texto exato)"],
  ["{{ FORMA_PAGAMENTO }}", "Forma de pagamento (texto exato)"],
  ["{{ DATA }}", "Data por extenso"],
  ["{{ CONTEUDO }}", "Corpo integral gerado pelo sistema (fallback)"],
];

export default function DocTemplates() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("contrato_honorarios");
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => { const r = await api.get("/document-templates"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file.name.toLowerCase().endsWith(".docx")) { toast.error("Envie um arquivo .docx"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("doc_type", type); fd.append("title", title || file.name);
      const r = await api.post("/document-templates", fd, { headers: {"Content-Type": "multipart/form-data"} });
      toast.success("Modelo enviado e ativado");
      setTitle("");
      await load();
      // Auto-open preview right after upload
      openPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha no upload"); }
    finally { setUploading(false); }
  };

  const openPreview = async (t) => {
    setPreviewLoading(true);
    setPreview({ id: t.id, title: t.title, doc_type: t.doc_type, rendered_text: "" });
    try {
      const r = await api.get(`/document-templates/${t.id}/preview`);
      setPreview({ ...r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar preview");
      setPreview(null);
    } finally { setPreviewLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este modelo?")) return;
    await api.delete(`/document-templates/${id}`); load();
  };

  const toggle = async (t) => {
    await api.patch(`/document-templates/${t.id}`, { active: !t.active }); load();
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col p-3 sm:p-6 lg:p-8 font-sans">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-semibold text-slate-900">Modelos DOCX</h1>
        <p className="text-slate-500 mt-1">Envie um <b>.docx</b> com o seu texto padrão e variáveis entre chaves duplas. Quando um modelo estiver ativo, a exportação DOCX daquele tipo usa <b>o seu texto</b> em vez do gerador embutido.</p>
      </div>

      <section className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
        <h2 className="font-heading text-lg font-semibold mb-4">Novo modelo</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">Tipo de documento</div>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="dt-type"
              className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <div className="text-xs font-medium text-slate-600 mb-1">Título (opcional)</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="dt-title"
              placeholder="Ex.: Contrato padrão civil"
              className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
          </label>
        </div>
        <input ref={fileRef} type="file" accept=".docx" hidden
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="dt-file-input"/>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="btn-upload-tpl"
          className="mt-4 w-full h-11 rounded-xl gradient-primary text-white text-sm font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition disabled:opacity-60">
          <Upload className="w-4 h-4"/> {uploading ? "Enviando..." : "Enviar arquivo .docx"}
        </button>
      </section>

      <section className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
        <h2 className="font-heading text-lg font-semibold mb-3">Variáveis disponíveis</h2>
        <p className="text-xs text-slate-500 mb-4">Cole estas variáveis no seu documento Word. Elas serão substituídas automaticamente na exportação.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {PLACEHOLDERS.map(([k, d]) => (
            <div key={k} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
              <code className="text-xs bg-white px-2 py-1 rounded border border-slate-200 text-[#0f4c5c] font-mono whitespace-nowrap">{k}</code>
              <div className="text-xs text-slate-600">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-lg font-semibold mb-3">Meus modelos</h2>
        {items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-500 text-sm">Nenhum modelo enviado ainda.</div>
        ) : (
          <ul className="space-y-3">
            {items.map(t => (
              <li key={t.id} className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center gap-4" data-testid={`dt-${t.id}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-700"><FileText className="w-4 h-4"/></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {TYPES.find(x => x.value === t.doc_type)?.label} · {new Date(t.created_at).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <button onClick={() => openPreview(t)} className="p-2 rounded-lg text-slate-600 hover:bg-slate-50 border border-slate-100" data-testid={`preview-${t.id}`} title="Ver preview">
                  <Eye className="w-4 h-4"/>
                </button>
                <button onClick={() => toggle(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${t.active?'bg-emerald-50 border-emerald-200 text-emerald-700':'bg-white text-slate-500 border-slate-200'}`}
                  data-testid={`toggle-${t.id}`}>
                  {t.active ? <><CheckCircle2 className="w-3 h-3 inline -mt-0.5 mr-1"/>Ativo</> : "Inativo"}
                </button>
                <button onClick={() => del(t.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50" data-testid={`del-${t.id}`}><Trash2 className="w-4 h-4"/></button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {preview && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="preview-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#0f4c5c] font-semibold">Preview do modelo</div>
                <div className="font-heading text-lg font-semibold text-slate-900">{preview.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">Renderizado com dados de exemplo — para conferir se as variáveis serão substituídas corretamente.</div>
              </div>
              <button onClick={() => setPreview(null)} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="preview-close">
                <X className="w-4 h-4"/>
              </button>
            </div>
            <div className="p-6 overflow-auto bg-slate-50 flex-1">
              <div className="doc-page">
                {previewLoading ? (
                  <div className="text-center py-16 text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Renderizando...</div>
                ) : (
                  <div className="font-doc whitespace-pre-wrap">{preview.rendered_text}</div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 bg-white text-xs text-slate-500 flex items-center justify-between">
              <span>Dados de exemplo: João da Silva Ferreira · R$ 5.000,00 · três parcelas</span>
              <button onClick={() => setPreview(null)} data-testid="preview-ok"
                className="px-4 h-9 rounded-lg gradient-primary text-white text-xs font-medium hover:-translate-y-0.5 transition">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
